import store from "../../store";
import config from "@/config/brands";
import { get } from "../../utils";
import set from "lodash/set";
import Vue from "vue";
import { GOOGLE_DL_NAME } from "../../config/constants";

/**
 * Abstracciones para GTM
 * Acciones generales
 */
export const GTMFunctions = {
  /**
   * Realiza el push al datalayer de GTM
   * @param {Object} data a enviar al datalayer
   */
  enviar(data) {
    if (config.googleGTMId) {
      // Evitar duplicados en pushes
      const { length, [length - 1]: lastEvent } = window[GOOGLE_DL_NAME];
      const lastElement_interactionLevel = get(
        lastEvent,
        "eventParams.application_interactionLevel",
        1
      );
      const currentElement_interactionLevel = get(
        data,
        "eventParams.application_interactionLevel",
        2
      );

      const lastElement_pageName = get(
        lastEvent,
        "eventParams.application_pageInfo_pageName",
        1
      );
      const currentElement_pageName = get(
        data,
        "eventParams.application_pageInfo_pageName",
        2
      );

      if (
        lastElement_interactionLevel !== currentElement_interactionLevel ||
        lastElement_pageName !== currentElement_pageName
      ) {
        window[GOOGLE_DL_NAME] = window[GOOGLE_DL_NAME] || [];
        window[GOOGLE_DL_NAME].push({ event: "ga4.trackEvent", ...data });
        Vue.$log.info("GTM", { event: "ga4.trackEvent", ...data });
      }
    }
  },
  /**
   * Construye el pagename para GTM de acuerdo al motor
   * @param {String} level2
   * @returns {String} Pagename ya formateado
   */
  pageName(...args) {
    if (!args?.length) {
      return GTMFunctions.appType();
    }
    const niveles = args.reduce(
      (acc, cu) => (cu ? `${acc} :: ${cu}` : acc),
      GTMFunctions.appType()
    );
    return niveles;
  },
  /**
   * Obtiene el nombre del motor / aplicación para analytics
   * @returns {String} App Name ya formateado
   */
  appName() {
    return config.analyticsName;
  },
  /**
   * Obtiene el nombre del motor / aplicación para analytics
   * @returns {String} App Type ya formateado
   */
  appType() {
    return config.analyticsType;
  },
  /**
   * Obtiene el nombre de la ruta / vista para analytics
   * @returns {String} Nombre ruta ya formateado
   */
  level2() {
    return get(
      store,
      "state.route.meta.config.analyticsName",
      get(store, "state.route.name", "undefined")
    );
  },
  /**
   * Formatear eventParams para eventos genéricos
   * @param {String} interactionLevel Nombre del elemento
   * @param {String} level3 Nombre sub-sección (como modales)
   * @returns eventParams formateado
   */
  commonEventParamsInteraction(interactionLevel, level2, application_typology) {
    const eventParams = {
      page_pageInfo_pageName: GTMFunctions.pageName(GTMFunctions.level2()),
      page_pageInfo_level1: GTMFunctions.appType(),
      page_pageInfo_level2: level2 || GTMFunctions.level2(),
      application_interactionLevel: interactionLevel
    };

    if (application_typology) {
      // Sub-aplications (biometria, solicitud, otp, etc)
      set(eventParams, "application_typology", application_typology);
    }

    return eventParams;
  },

  /**
   * Formatear eventParams para app_started y app_step_X
   * @param {Object} eventData datos del evento
   * @param {String} appStep application_step
   * @param {String} appType application_type
   * @param {String} pageName page_name
   */
  commonEventParamsAppStep(eventData, appStep, appType, pageName) {
    const analyticsType = get(eventData, "to.meta.config.analyticsType", "");
    let eventParams;
    if (pageName) {
      eventParams = {
        page_pageInfo_pageName: GTMFunctions.pageName(
          pageName,
          GTMFunctions.level2()
        ),
        application_application_name: GTMFunctions.appName(),
        application_application_type: appType || analyticsType
      };
    } else {
      eventParams = {
        page_pageInfo_pageName: GTMFunctions.pageName(GTMFunctions.level2()),
        application_application_name: GTMFunctions.appName(),
        application_application_type:
          appType || analyticsType || GTMFunctions.appType()
      };
    }

    if (appStep) {
      eventParams.application_step = appStep;
    }

    return { ...eventParams };
  },

  commonEventParamsAppError(level2, errorPage, level3) {
    const eventParams = {
      page_pageInfo_pageName: GTMFunctions.pageName(
        level2 || GTMFunctions.level2(),
        level3
      ),
      page_pageInfo_level1: GTMFunctions.appName(),
      page_pageInfo_level2: level2 || GTMFunctions.level2(),
      page_pageInfo_errorPage: errorPage
    };

    if (level3) {
      // Sub-sección (como modales)
      set(eventParams, "page_pageInfo_level3", level3);
    }

    return eventParams;
  },

  /**
   * Formatear eventParams para productos y segmentos
   */
  commonEventParamsProductSegment(params) {
    let eventParams = {};
    if (params.includes("products_attributes_0_productName")) {
      eventParams["products_attributes_0_productName"] = window[
        "googleGTMDatalayer"
      ].findLast(
        item =>
          item.eventName === "interaction_link" &&
          item.eventParams.page_pageInfo_pageName ==
            "referente :: elegir opcion"
      ).eventParams.application_interactionLevel;
    }
    if (params.includes("application_interactionLevel")) {
      eventParams["application_interactionLevel"] = window[
        "googleGTMDatalayer"
      ].findLast(
        item =>
          item.eventName === "interaction_link" &&
          item.eventParams.page_pageInfo_pageName ==
            "referente :: elegir premio"
      ).eventParams.application_interactionLevel;
    }

    return eventParams;
  }
};

export const GTM = {
  enable() {},
  init() {
    if (config.googleGTMId) {
      (function (window, document, script, dlName, gtmId) {
        window[dlName] = window[dlName] || [];
        window[dlName].push({
          "gtm.start": new Date().getTime(),
          event: "gtm.js"
        });
        let f = document.getElementsByTagName(script)[0],
          j = document.createElement(script),
          dl = dlName != "dataLayer" ? "&l=" + dlName : "";
        j.async = true;
        j.src = "https://www.googletagmanager.com/gtm.js?id=" + gtmId + dl;
        f.parentNode.insertBefore(j, f);
      })(window, document, "script", GOOGLE_DL_NAME, config.googleGTMId);
    }
    Vue.$log.info("GTM INIT");
  },
  wait() {
    return true;
  },
  to_paso1(eventData) {
    const eventParams = GTMFunctions.commonEventParamsAppStep(
      eventData,
      "0 verificar"
    );

    GTMFunctions.enviar({
      eventName: "app_started",
      eventParams
    });
  },
  to_paso2(eventData) {
    const eventParams = GTMFunctions.commonEventParamsAppStep(
      eventData,
      "1 elegir opcion"
    );

    GTMFunctions.enviar({
      eventName: "app_step_1",
      eventParams
    });
  },
  to_paso3(eventData) {
    const eventParams = GTMFunctions.commonEventParamsAppStep(
      eventData,
      "2 elegir premio"
    );

    GTMFunctions.enviar({
      eventName: "app_step_2",
      eventParams
    });
  },
  to_paso4(eventData) {
    let eventParams = GTMFunctions.commonEventParamsAppStep(
      eventData,
      "3 compartir"
    );

    eventParams = {
      ...eventParams,
      ...GTMFunctions.commonEventParamsProductSegment([
        "products_attributes_0_productName",
        "application_interactionLevel"
      ])
    };
    GTMFunctions.enviar({
      eventName: "app_completed",
      eventParams
    });
  },

  /**
   * Evento de error backend
   * @param {Object} eventData
   */
  error_backend({
    component,
    errorDescription,
    errorType,
    pageName,
    pagePath
  }) {
    try {
      const eventParams = {
        page_pagelnfo_server: component,
        page_pagelnfo_level1:
          typeof errorDescription === "string"
            ? errorDescription
            : JSON.stringify(errorDescription),
        page_pagelnfo_pageName: GTMFunctions.pageName(
          get(config, `pasos.${pageName}.analyticsName`, "")
        ),
        page_pagelnfo_pagePath: pagePath,
        application_errorType: errorType
      };

      GTMFunctions.enviar({
        eventName: "error_backend",
        eventParams: { ...eventParams }
      });
    } catch (error) {
      Vue.$log.error("Error enviando evento error_backend: ", error);
    }
  },
  /**
   * Evento de links, clicks a botones
   * @param {Object} eventData (name, level3)
   */
  interaction_link({ name, level2, level3 }) {
    const eventParams = GTMFunctions.commonEventParamsInteraction(
      name,
      level2,
      level3
    );

    GTMFunctions.enviar({
      eventName: "interaction_link",
      eventParams
    });
  }
};
